import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Layers, 
  Compass, 
  Save, 
  Plus, 
  Trash2, 
  Edit3, 
  Clock, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  X,
  Tag
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationCurriculumOverviewForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Curriculum Overview',
    title: 'National & International Curriculum Pathway',
    summary: 'We blend the national curriculum with Cambridge perspectives so every student grows in faith, character, and academic readiness from Preschool to Secondary.',
    note: 'Each level is designed to build upon previous learning while preparing students for the next stage of their educational journey.',
    pillars: [],
    tracks: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  // Modals state
  const [editingTrackIndex, setEditingTrackIndex] = useState(null);
  const [modalTrackData, setModalTrackData] = useState(null);
  const [newHighlight, setNewHighlight] = useState('');
  const [newComponent, setNewComponent] = useState('');

  const [editingPillarIndex, setEditingPillarIndex] = useState(null);
  const [modalPillarData, setModalPillarData] = useState(null);

  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  useEffect(() => {
    const initializeForm = async () => {
      await loadMediaMap();
      await loadSection();
    };
    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[EducationCurriculumOverviewForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let curriculumSection = null;
      const headers = getAuthHeaders();
      
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { credentials: 'include', headers }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          curriculumSection = result.data.find(s => s.section_type === 'education-curriculum-overview-shell');
        }
      }
      
      if (!curriculumSection) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { credentials: 'include', headers }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            curriculumSection = result.data.find(s => s.section_type === 'education-curriculum-overview-shell');
          }
        }
      }
      
      if (!curriculumSection) {
        throw new Error('Curriculum overview section not found');
      }

      const currentSectionId = curriculumSection.id;
      setSectionId(currentSectionId);

      let published = curriculumSection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }

      const publishedEn = published?.en || {};
      const initialData = {
        kicker: publishedEn.kicker || 'Curriculum Overview',
        title: publishedEn.title || 'National & International Curriculum Pathway',
        summary: publishedEn.summary || 'We blend the national curriculum with Cambridge perspectives so every student grows in faith, character, and academic readiness from Preschool to Secondary.',
        note: publishedEn.note || 'Each level is designed to build upon previous learning while preparing students for the next stage of their educational journey.',
        pillars: (Array.isArray(publishedEn.pillars) ? publishedEn.pillars : []).filter(p => p && typeof p === 'object' && p.delete === undefined && !p.deleted && !p._deleted && (p.title || '').trim() && (p.title || '').trim().toLowerCase() !== 'xxx'),
        tracks: (Array.isArray(publishedEn.tracks) ? publishedEn.tracks : []).filter(t => t && typeof t === 'object' && t.delete === undefined && !t.deleted && !t._deleted && (t.title || '').trim() && (t.title || '').trim().toLowerCase() !== 'xxx').map(t => ({
          ...t,
          highlights: Array.isArray(t.highlights) ? [...t.highlights] : [],
          components: Array.isArray(t.components) ? [...t.components] : []
        }))
      };

      const pendingMap = {};

      try {
        const pendingResponse = await fetch(
          `${apiBase}/cms/section/${currentSectionId}/with-pending`,
          { credentials: 'include', headers }
        );
        
        if (pendingResponse.ok) {
          const pendingResult = await pendingResponse.json();
          if (pendingResult.success && pendingResult.data && Array.isArray(pendingResult.data.pending_changes)) {
            const { pending_changes } = pendingResult.data;
            pending_changes.forEach(change => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (change.field_id === 'overview' || change.field_id === 'overview.update') {
                  pendingMap['overview'] = true;
                  pendingMap['kicker'] = true;
                  pendingMap['title'] = true;
                  pendingMap['summary'] = true;
                  pendingMap['note'] = true;
                  if (val && typeof val === 'object') {
                    if (val.kicker) initialData.kicker = val.kicker;
                    if (val.title) initialData.title = val.title;
                    if (val.summary) initialData.summary = val.summary;
                    if (val.note) initialData.note = val.note;
                  }
                } else if (['kicker', 'title', 'summary', 'note'].includes(change.field_id)) {
                  pendingMap[change.field_id] = true;
                  if (val) initialData[change.field_id] = val;
                } else if (change.field_id && change.field_id.includes('tracks.')) {
                  const match = change.field_id.match(/tracks\.(\d+)\./);
                  if (match) {
                    const tIdx = parseInt(match[1], 10);
                    pendingMap[`track_${tIdx}`] = true;
                    if (val && initialData.tracks[tIdx]) {
                      initialData.tracks[tIdx] = { ...initialData.tracks[tIdx], ...val };
                    }
                  }
                } else if (change.field_id && change.field_id.includes('pillars.')) {
                  const match = change.field_id.match(/pillars\.(\d+)\./);
                  if (match) {
                    const pIdx = parseInt(match[1], 10);
                    pendingMap[`pillar_${pIdx}`] = true;
                    if (val && initialData.pillars[pIdx]) {
                      initialData.pillars[pIdx] = { ...initialData.pillars[pIdx], ...val };
                    }
                  }
                }
              }
            });
          }
        }
      } catch (err) {
        console.warn('Pending changes check failed:', err.message);
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingMap);
    } catch (err) {
      console.error('[EducationCurriculumOverviewForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitSingleField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Curriculum section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: label || fieldName.replace(/_/g, ' '),
          new_value: formData[fieldName],
          old_value: originalData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleAddTrack = () => {
    const newTrack = {
      title: '',
      subtitle: '',
      description: '',
      image: '',
      highlights: [],
      components: []
    };
    const nextIdx = formData.tracks.length;
    setFormData(prev => ({
      ...prev,
      tracks: [...prev.tracks, newTrack]
    }));
    setEditingTrackIndex(nextIdx);
    setModalTrackData(newTrack);
    setNewHighlight('');
    setNewComponent('');
  };

  const handleEditTrack = (idx) => {
    const track = formData.tracks[idx];
    if (!track) return;
    setEditingTrackIndex(idx);
    setModalTrackData({
      ...track,
      highlights: Array.isArray(track.highlights) ? [...track.highlights] : [],
      components: Array.isArray(track.components) ? [...track.components] : []
    });
    setNewHighlight('');
    setNewComponent('');
  };

  const handleCloseTrackModal = () => {
    if (editingTrackIndex !== null && editingTrackIndex >= originalData.tracks.length) {
      setFormData(prev => ({
        ...prev,
        tracks: prev.tracks.filter((_, i) => i !== editingTrackIndex)
      }));
    }
    setEditingTrackIndex(null);
    setModalTrackData(null);
    setNewHighlight('');
    setNewComponent('');
  };

  const handleAddTrackHighlight = () => {
    if (!newHighlight.trim()) return;
    setModalTrackData(prev => ({
      ...prev,
      highlights: [...(prev.highlights || []), newHighlight.trim()]
    }));
    setNewHighlight('');
  };

  const handleRemoveTrackHighlight = (hIdx) => {
    setModalTrackData(prev => ({
      ...prev,
      highlights: (prev.highlights || []).filter((_, i) => i !== hIdx)
    }));
  };

  const handleAddTrackComponent = () => {
    if (!newComponent.trim()) return;
    setModalTrackData(prev => ({
      ...prev,
      components: [...(prev.components || []), newComponent.trim()]
    }));
    setNewComponent('');
  };

  const handleRemoveTrackComponent = (cIdx) => {
    setModalTrackData(prev => ({
      ...prev,
      components: (prev.components || []).filter((_, i) => i !== cIdx)
    }));
  };

  const handleDeleteTrack = async (index) => {
    if (pendingFields[`track_${index}`]) {
      toast.info('This track is awaiting approval');
      return;
    }

    const trackToDelete = formData.tracks[index];
    if (!trackToDelete) return;

    if (!window.confirm(`Delete track: ${trackToDelete.title || index + 1}?`)) return;

    setSubmitting(prev => ({ ...prev, [`del_track_${index}`]: true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `tracks.${index}.delete`,
          field_name: `Delete Curriculum Track - ${trackToDelete.title || index + 1}`,
          old_value: trackToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Track deletion submitted for approval`);
        setPendingFields(prev => ({ ...prev, [`track_${index}`]: true }));
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_track_${index}`]: false }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                setModalTrackData(prev => ({ ...prev, image: mediaId }));
                toast.success('Track image uploaded successfully');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload?section_type=education-curriculum-overview-shell`);
      xhr.withCredentials = true;
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitTrackModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const trackIndex = editingTrackIndex;
    const track = modalTrackData;
    const trackKey = `track_${trackIndex}`;

    if (!track || !track.title?.trim() || !track.description?.trim()) {
      toast.warning('Please enter both Title and Description');
      return;
    }

    setSubmitting(prev => ({ ...prev, [trackKey]: true }));

    try {
      const isNew = trackIndex >= originalData.tracks.length;
      const op = isNew ? 'add' : 'update';

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `tracks.${trackIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Academic Track - ${track.title}`,
            old_value: isNew ? null : (originalData.tracks[trackIndex] || {}),
            new_value: track,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Track "${track.title}" submitted for approval`);
        setPendingFields(prev => ({ ...prev, [trackKey]: true }));
        setFormData(prev => ({
          ...prev,
          tracks: prev.tracks.map((t, i) => i === trackIndex ? track : t)
        }));
        setOriginalData(prev => {
          const next = [...prev.tracks];
          next[trackIndex] = track;
          return { ...prev, tracks: next };
        });
        setEditingTrackIndex(null);
        setModalTrackData(null);
      } else {
        if (response.status === 409) {
          toast.error('This track already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [trackKey]: false }));
    }
  };

  const handleAddPillar = () => {
    const newPillar = { title: '', description: '', icon: '' };
    const nextIdx = formData.pillars.length;
    setFormData(prev => ({
      ...prev,
      pillars: [...prev.pillars, newPillar]
    }));
    setEditingPillarIndex(nextIdx);
    setModalPillarData(newPillar);
  };

  const handleEditPillar = (idx) => {
    const pillar = formData.pillars[idx];
    if (!pillar) return;
    setEditingPillarIndex(idx);
    setModalPillarData({ ...pillar });
  };

  const handleClosePillarModal = () => {
    if (editingPillarIndex !== null && editingPillarIndex >= originalData.pillars.length) {
      setFormData(prev => ({
        ...prev,
        pillars: prev.pillars.filter((_, i) => i !== editingPillarIndex)
      }));
    }
    setEditingPillarIndex(null);
    setModalPillarData(null);
  };

  const handleDeletePillar = async (index) => {
    if (pendingFields[`pillar_${index}`]) {
      toast.info('This pillar is awaiting approval');
      return;
    }

    const pillarToDelete = formData.pillars[index];
    if (!pillarToDelete) return;

    if (!window.confirm(`Delete pillar: ${pillarToDelete.title || index + 1}?`)) return;

    setSubmitting(prev => ({ ...prev, [`del_pillar_${index}`]: true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `pillars.${index}.delete`,
          field_name: `Delete Curriculum Pillar - ${pillarToDelete.title || index + 1}`,
          old_value: pillarToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Pillar deletion submitted for approval`);
        setPendingFields(prev => ({ ...prev, [`pillar_${index}`]: true }));
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_pillar_${index}`]: false }));
    }
  };

  const handleSubmitPillarModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const pillarIndex = editingPillarIndex;
    const pillar = modalPillarData;
    const pillarKey = `pillar_${pillarIndex}`;

    if (!pillar || !pillar.title?.trim() || !pillar.description?.trim()) {
      toast.warning('Please enter both Title and Description');
      return;
    }

    setSubmitting(prev => ({ ...prev, [pillarKey]: true }));

    try {
      const isNew = pillarIndex >= originalData.pillars.length;
      const op = isNew ? 'add' : 'update';

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `pillars.${pillarIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Pillar - ${pillar.title}`,
            old_value: isNew ? null : (originalData.pillars[pillarIndex] || {}),
            new_value: pillar,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Pillar "${pillar.title}" submitted for approval`);
        setPendingFields(prev => ({ ...prev, [pillarKey]: true }));
        setFormData(prev => ({
          ...prev,
          pillars: prev.pillars.map((p, i) => i === pillarIndex ? pillar : p)
        }));
        setOriginalData(prev => {
          const next = [...prev.pillars];
          next[pillarIndex] = pillar;
          return { ...prev, pillars: next };
        });
        setEditingPillarIndex(null);
        setModalPillarData(null);
      } else {
        if (response.status === 409) {
          toast.error('This pillar already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [pillarKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Curriculum Overview...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <BookOpen size={22} />
          </div>
          <div>
            <h2 className="unified-title">Curriculum Overview</h2>
            <p className="unified-subtitle">Manage curriculum framework headlines, foundational pillars, and academic track stages</p>
          </div>
        </div>
      </div>

      {/* Sub-Navigation Tabs */}
      <div className="unified-tab-bar" style={{ marginBottom: '24px' }}>
        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('overview')}
        >
          <BookOpen size={15} style={{ marginRight: '6px' }} />
          <span>Curriculum Overview</span>
        </button>

        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'pillars' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('pillars')}
        >
          <Layers size={15} style={{ marginRight: '6px' }} />
          <span>Curriculum Pillars ({formData.pillars.length})</span>
        </button>

        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'tracks' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('tracks')}
        >
          <Compass size={15} style={{ marginRight: '6px' }} />
          <span>Academic Tracks ({formData.tracks.length})</span>
        </button>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeSubTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Curriculum Framework Header & Overview
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Primary kicker badge, headline, summary, and pedagogical continuum note explaining the dual pathway
              </p>
            </div>
            {pendingFields['overview'] && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>

          {/* Section Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Tag / Kicker</label>
              {pendingFields['kicker'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="60"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={pendingFields['kicker'] || submitting['kicker']}
                placeholder="e.g. Curriculum Overview"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('kicker', 'Section Kicker')}
                disabled={submitting['kicker'] || pendingFields['kicker'] || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting['kicker'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Curriculum Headline</label>
              {pendingFields['title'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="200"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={pendingFields['title'] || submitting['title']}
                placeholder="e.g. National & International Curriculum Pathway"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('title', 'Curriculum Headline')}
                disabled={submitting['title'] || pendingFields['title'] || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting['title'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Curriculum Summary</label>
              {pendingFields['summary'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                rows="4"
                className="unified-textarea"
                value={formData.summary}
                onChange={(e) => handleFieldChange('summary', e.target.value)}
                disabled={pendingFields['summary'] || submitting['summary']}
                placeholder="Detailed synthesis of our academic framework, standards, and global accreditations..."
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={() => handleSubmitSingleField('summary', 'Curriculum Summary')}
                disabled={submitting['summary'] || pendingFields['summary'] || formData.summary === originalData.summary}
              >
                <Save size={14} /> {submitting['summary'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Pedagogical Continuum Note</label>
              {pendingFields['note'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                rows="3"
                className="unified-textarea"
                value={formData.note}
                onChange={(e) => handleFieldChange('note', e.target.value)}
                disabled={pendingFields['note'] || submitting['note']}
                placeholder="e.g. Each level is designed to build upon previous learning while preparing students for the next stage of their educational journey."
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={() => handleSubmitSingleField('note', 'Pedagogical Continuum Note')}
                disabled={submitting['note'] || pendingFields['note'] || formData.note === originalData.note}
              >
                <Save size={14} /> {submitting['note'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PILLARS */}
      {activeSubTab === 'pillars' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Curriculum Pillars ({formData.pillars.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Key pedagogical foundation blocks anchoring our curriculum (National Foundations, Cambridge Perspective, Holistic Growth)
              </p>
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleAddPillar}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} /> Add Pillar
            </button>
          </div>

          {formData.pillars.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
              <Layers size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
              <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Pillars Defined</h4>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Pillar" above to define curriculum pillars</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {formData.pillars.map((pillar, idx) => {
                const isPending = pendingFields[`pillar_${idx}`];

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '18px 24px',
                      borderRadius: '14px',
                      border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                      borderLeft: isPending ? '5px solid #f59e0b' : `5px solid #6366f1`,
                      background: isPending ? '#fffdf7' : '#ffffff',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '800', color: '#6366f1', background: '#eef2ff', padding: '2px 8px', borderRadius: '6px' }}>
                          Pillar 0{idx + 1}
                        </span>
                        <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: '800', color: '#0f172a' }}>
                          {pillar.title}
                        </h4>
                        {isPending && (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                            <Clock size={10} /> Pending Review
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        {pillar.description}
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => {
                          if (isPending) {
                            toast.info('This pillar is awaiting approval');
                            return;
                          }
                          handleEditPillar(idx);
                        }}
                        disabled={isPending}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Edit3 size={14} /> Edit
                      </button>
                      {!isPending && (
                        <button
                          type="button"
                          onClick={() => handleDeletePillar(idx)}
                          disabled={submitting[`del_pillar_${idx}`]}
                          style={{
                            padding: '8px 12px',
                            color: '#ef4444',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Delete Pillar"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: TRACKS */}
      {activeSubTab === 'tracks' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Academic Pathways & Curriculum Tracks ({formData.tracks.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Curriculum stage cards (Preschool, Kindergarten, Primary, Secondary) with highlights, components, and photos
              </p>
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleAddTrack}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} /> Add Track
            </button>
          </div>

          {formData.tracks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
              <Compass size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
              <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Learning Tracks Found</h4>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Track" above to create an academic pathway</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {formData.tracks.map((track, idx) => {
                const isPending = pendingFields[`track_${idx}`];
                const photoUrl = getMediaUrl(track.image);
                const highlightsCount = Array.isArray(track.highlights) ? track.highlights.length : 0;
                const componentsCount = Array.isArray(track.components) ? track.components.length : 0;

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '20px 24px',
                      borderRadius: '14px',
                      border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                      borderLeft: isPending ? '5px solid #f59e0b' : `5px solid #2563eb`,
                      background: isPending ? '#fffdf7' : '#ffffff',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Thumbnail */}
                    <div
                      style={{
                        width: '120px',
                        height: '85px',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: '#0f172a',
                        border: '1px solid #cbd5e1',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={track.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage({ src: photoUrl, title: track.title })}
                        />
                      ) : (
                        <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <ImageIcon size={22} />
                          <span style={{ fontSize: '10px' }}>No photo</span>
                        </div>
                      )}
                      {photoUrl && (
                        <button
                          type="button"
                          onClick={() => setLightboxImage({ src: photoUrl, title: track.title })}
                          style={{
                            position: 'absolute',
                            bottom: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            border: 'none',
                            padding: '3px 5px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="View Photo"
                        >
                          <Eye size={11} />
                        </button>
                      )}
                    </div>

                    {/* Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11.5px', background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '6px', fontWeight: '800' }}>
                          Stage 0{idx + 1}
                        </span>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                          {track.title}
                        </h4>
                        {track.subtitle && track.subtitle !== track.title && (
                          <span style={{ fontSize: '11.5px', background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontWeight: '600' }}>
                            {track.subtitle}
                          </span>
                        )}
                        {isPending && (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                            <Clock size={10} /> Pending Review
                          </span>
                        )}
                      </div>
                      
                      <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#475569', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {track.description}
                      </p>

                      {/* Highlights & Components Badges */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {highlightsCount > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>Highlights:</span>
                            {track.highlights.slice(0, 2).map((hl, hIdx) => (
                              <span key={hIdx} style={{ fontSize: '11px', background: '#f8fafc', color: '#334155', padding: '1px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                • {hl}
                              </span>
                            ))}
                            {highlightsCount > 2 && (
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{highlightsCount - 2} more</span>
                            )}
                          </div>
                        )}

                        {componentsCount > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11.5px', color: '#047857', fontWeight: '600' }}>Structure:</span>
                            {track.components.slice(0, 2).map((comp, cIdx) => (
                              <span key={cIdx} style={{ fontSize: '11px', background: '#ecfdf5', color: '#065f46', padding: '1px 8px', borderRadius: '4px', border: '1px solid #a7f3d0' }}>
                                {comp}
                              </span>
                            ))}
                            {componentsCount > 2 && (
                              <span style={{ fontSize: '11px', color: '#047857' }}>+{componentsCount - 2} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => {
                          if (isPending) {
                            toast.info('This track is awaiting approval');
                            return;
                          }
                          handleEditTrack(idx);
                        }}
                        disabled={isPending}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Edit3 size={14} /> Edit
                      </button>
                      {!isPending && (
                        <button
                          type="button"
                          onClick={() => handleDeleteTrack(idx)}
                          disabled={submitting[`del_track_${idx}`]}
                          style={{
                            padding: '8px 12px',
                            color: '#ef4444',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Delete Track"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Track Modal */}
      {editingTrackIndex !== null && modalTrackData && (
        <div className="unified-modal-overlay" onClick={handleCloseTrackModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  {modalTrackData.title ? `Edit Track: ${modalTrackData.title}` : 'Add Academic Track'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure title, description, key learning highlights, structure components, and photo
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseTrackModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Photo preview & upload */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ width: '120px', height: '80px', borderRadius: '8px', overflow: 'hidden', background: '#0f172a', border: '1px solid #cbd5e1', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {modalTrackData.image && getMediaUrl(modalTrackData.image) ? (
                    <img
                      src={getMediaUrl(modalTrackData.image)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon size={28} color="#94a3b8" />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="unified-label" style={{ marginBottom: '6px' }}>Track Photo</label>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#64748b' }}>
                    JPG, PNG, WebP • Max 5MB • Recommended: 600×400px
                  </p>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                      type="file"
                      id="track-photo-upload"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={imageUploading}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor="track-photo-upload" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                      <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : 'Browse & Upload Photo'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Track Name / Stage Title</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalTrackData.title || ''}
                  onChange={(e) => setModalTrackData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Primary Program / Secondary"
                />
              </div>

              {/* Subtitle / Focus */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Subtitle / Grades</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalTrackData.subtitle || ''}
                  onChange={(e) => setModalTrackData(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="e.g. Grades 7-9 • Intra & Extra Curriculum"
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Track Overview & Description</label>
                <textarea
                  className="unified-textarea"
                  rows="4"
                  value={modalTrackData.description || ''}
                  onChange={(e) => setModalTrackData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Comprehensive description of the curriculum focus at this stage..."
                />
              </div>

              {/* Highlights */}
              <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Key Learning Highlights</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {(modalTrackData.highlights || []).map((hl, hIdx) => (
                    <div
                      key={hIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        background: '#ffffff',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px'
                      }}
                    >
                      <span>• {hl}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTrackHighlight(hIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newHighlight}
                    onChange={(e) => setNewHighlight(e.target.value)}
                    placeholder="Add a track highlight bullet point..."
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTrackHighlight(); } }}
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddTrackHighlight}
                    disabled={!newHighlight.trim()}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>

              {/* Structure Components */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <Tag size={14} color="#047857" />
                  <label className="unified-label" style={{ margin: 0 }}>Curriculum Structure Components (Optional)</label>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#64748b' }}>
                  Curriculum pillars or formal component tags (e.g. Intra Curriculum, Co-Curriculum, Extra Curriculum, SDP)
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {(modalTrackData.components || []).map((comp, cIdx) => (
                    <span
                      key={cIdx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: '#ecfdf5',
                        color: '#065f46',
                        borderRadius: '6px',
                        border: '1px solid #a7f3d0',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      <span>{comp}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTrackComponent(cIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newComponent}
                    onChange={(e) => setNewComponent(e.target.value)}
                    placeholder="e.g. Intra Curriculum (Kurikulum Inti)"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTrackComponent(); } }}
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddTrackComponent}
                    disabled={!newComponent.trim()}
                  >
                    <Plus size={14} /> Add Component
                  </button>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseTrackModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitTrackModal}
                disabled={submitting[`track_${editingTrackIndex}`] || !modalTrackData.title?.trim() || imageUploading}
              >
                <Save size={14} /> {submitting[`track_${editingTrackIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pillar Modal */}
      {editingPillarIndex !== null && modalPillarData && (
        <div className="unified-modal-overlay" onClick={handleClosePillarModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  {modalPillarData.title ? `Edit Pillar: ${modalPillarData.title}` : 'Add Curriculum Pillar'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Update foundational pillar name and educational explanation
                </p>
              </div>
              <button
                type="button"
                onClick={handleClosePillarModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Pillar Name</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalPillarData.title || ''}
                  onChange={(e) => setModalPillarData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. NATIONAL FOUNDATIONS"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Pillar Explanation</label>
                <textarea
                  className="unified-textarea"
                  rows="4"
                  value={modalPillarData.description || ''}
                  onChange={(e) => setModalPillarData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Explain how this pillar is enacted in the classroom and curriculum..."
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleClosePillarModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitPillarModal}
                disabled={submitting[`pillar_${editingPillarIndex}`] || !modalPillarData.title?.trim()}
              >
                <Save size={14} /> {submitting[`pillar_${editingPillarIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Track photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
